import type { ISODate } from "../types.js";
import { addDays, dayOfWeek, nthWeekdayOfMonth, toISODate } from "./date.js";

/**
 * Japanese public holidays computed from the current 祝日法 rules.
 * Valid for 2022 onwards (the 2020/2021 Olympic one-off moves are not modeled).
 * The equinox formulas are the standard approximations valid through 2099.
 */

const MONDAY = 1;

function vernalEquinoxDay(y: number): number {
  return Math.floor(20.8431 + 0.242194 * (y - 1980) - Math.floor((y - 1980) / 4));
}

function autumnalEquinoxDay(y: number): number {
  return Math.floor(23.2488 + 0.242194 * (y - 1980) - Math.floor((y - 1980) / 4));
}

/** Fixed and happy-monday holidays, before substitute/bridge rules. */
function baseHolidays(y: number): Map<ISODate, string> {
  const h = new Map<ISODate, string>();
  const put = (d: ISODate, name: string) => h.set(d, name);

  put(toISODate(y, 1, 1), "元日");
  put(nthWeekdayOfMonth(y, 1, MONDAY, 2), "成人の日");
  put(toISODate(y, 2, 11), "建国記念の日");
  put(toISODate(y, 2, 23), "天皇誕生日");
  put(toISODate(y, 3, vernalEquinoxDay(y)), "春分の日");
  put(toISODate(y, 4, 29), "昭和の日");
  put(toISODate(y, 5, 3), "憲法記念日");
  put(toISODate(y, 5, 4), "みどりの日");
  put(toISODate(y, 5, 5), "こどもの日");
  put(nthWeekdayOfMonth(y, 7, MONDAY, 3), "海の日");
  put(toISODate(y, 8, 11), "山の日");
  put(nthWeekdayOfMonth(y, 9, MONDAY, 3), "敬老の日");
  put(toISODate(y, 9, autumnalEquinoxDay(y)), "秋分の日");
  put(nthWeekdayOfMonth(y, 10, MONDAY, 2), "スポーツの日");
  put(toISODate(y, 11, 3), "文化の日");
  put(toISODate(y, 11, 23), "勤労感謝の日");
  return h;
}

/**
 * All public holidays of a year including 振替休日 (substitute holidays) and
 * 国民の休日 (a weekday sandwiched between two holidays).
 */
export function jpHolidays(y: number): Map<ISODate, string> {
  const h = baseHolidays(y);

  // 振替休日: a holiday on Sunday moves to the next day that is not a holiday.
  for (const d of [...h.keys()]) {
    if (dayOfWeek(d) !== 0) continue;
    let next = addDays(d, 1);
    while (h.has(next)) next = addDays(next, 1);
    h.set(next, "振替休日");
  }

  // 国民の休日: a non-holiday weekday with holidays on both sides.
  for (const d of [...h.keys()]) {
    const mid = addDays(d, 1);
    const after = addDays(d, 2);
    if (!h.has(mid) && h.has(after) && dayOfWeek(mid) !== 0) {
      h.set(mid, "国民の休日");
    }
  }

  return h;
}

/**
 * Days the Tokyo Stock Exchange is closed, excluding weekends:
 * public holidays plus the year-end/new-year break (12/31 - 1/3).
 */
export function jpxClosedDays(y: number): Set<ISODate> {
  const closed = new Set<ISODate>(jpHolidays(y).keys());
  closed.add(toISODate(y, 12, 31));
  closed.add(toISODate(y, 1, 2));
  closed.add(toISODate(y, 1, 3));
  return closed;
}
